import { put, takeLatest } from "redux-saga/effects";
import {
  fetchLapRequest, fetchLapSuccess, fetchLapFailure,
  fetchLapsRequest, fetchLapsSuccess, fetchLapsFailure,
  createLapRequest, createLapSuccess, createLapFailure,
  updateLapRequest, updateLapSuccess, updateLapFailure,
  deleteLapRequest, deleteLapSuccess, deleteLapFailure,
  publishLapRequest, publishLapSuccess, publishLapFailure
} from "./lapSlice";
import { fetchPublishedRequest } from "../../redux/published/publishedSolutionsSlice";
import { fastApiURL } from "../../utils/config";
import TokenManager from "../../utils/tokenManager";

function* fetchLapSaga(action) {
  try {
    const token = TokenManager.getAccessToken();
    const projectId = action.payload;

    const response = yield fetch(`${fastApiURL}/api/laps/project/${projectId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      if (response.status === 404) {
        yield put(fetchLapSuccess(null));
        return;
      }
      const errorData = yield response.json();
      throw new Error(errorData.detail || "Failed to fetch LAP.");
    }

    const result = yield response.json();
    

    let lapData = result.data || result; 
    
    if (Array.isArray(lapData)) {
        lapData = lapData.length > 0 ? lapData[0] : null;
    }

    yield put(fetchLapSuccess(lapData));
  } catch (error) {
    yield put(fetchLapFailure(error.message));
  }
}

function* fetchLapsSaga() {
  try {
    const token = TokenManager.getAccessToken();
    
    const response = yield fetch(`${fastApiURL}/api/laps/published`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      const errorData = yield response.json();
      throw new Error(errorData.detail || "Failed to fetch LAPs from Cassandra.");
    }

    const result = yield response.json();
    
    const lapsList = result.data || result || [];
    
    yield put(fetchLapsSuccess(lapsList));
  } catch (error) {
    yield put(fetchLapsFailure(error.message));
  }
}

function* createLapSaga(action) {
  try {
    const token = TokenManager.getAccessToken();
    const { projectId, data } = action.payload;

    const response = yield fetch(`${fastApiURL}/api/laps/project/${projectId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) throw new Error("Failed to create LAP");

    yield put(createLapSuccess());
    yield put(fetchLapRequest(projectId)); // Refresh data
  } catch (error) {
    yield put(createLapFailure(error.message));
  }
}

function* updateLapSaga(action) {
  try {
    const token = TokenManager.getAccessToken();
    const { lapId, projectId, data } = action.payload;

    const response = yield fetch(`${fastApiURL}/api/laps/${lapId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) throw new Error("Failed to update LAP");

    yield put(updateLapSuccess());
    yield put(fetchLapRequest(projectId)); // Refresh data
  } catch (error) {
    yield put(updateLapFailure(error.message));
  }
}

function* deleteLapSaga(action) {
  try {
    const token = TokenManager.getAccessToken();
    const { lapId, projectId } = action.payload;

    const response = yield fetch(`${fastApiURL}/api/laps/${lapId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) throw new Error("Failed to delete LAP");

    yield put(deleteLapSuccess());
    yield put(fetchLapRequest(projectId)); // Will now return 404 -> set to null
  } catch (error) {
    yield put(deleteLapFailure(error.message));
  }
}

function* publishLapSaga(action) {
  try {
    const token = TokenManager.getAccessToken();
    const { projectId, lap } = action.payload;
    const solutionIds = (lap?.solution_ids || []).slice(0, 3);

    for (const solutionId of solutionIds) {
      const response = yield fetch(`${fastApiURL}/api/drafts/${solutionId}/publish`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        console.warn(`Solution ${solutionId} might already be published (Status: ${response.status}). Skipping...`);
      }
    }
    
    const lapUpdateResponse = yield fetch(`${fastApiURL}/api/laps/${lap.id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        title: lap.title,
        description: lap.description,
        solution_ids: lap.solution_ids,
        status: "published",
      }),
    });

    if (!lapUpdateResponse.ok) {
      const errorData = yield lapUpdateResponse.json();
      throw new Error(errorData.detail || "Failed to mark LAP as published");
    }
    yield put(publishLapSuccess());
    yield put(fetchPublishedRequest(projectId));
    yield put(fetchLapRequest(projectId));
    yield put(fetchLapsRequest());
  } catch (error) {
    yield put(publishLapFailure(error.message));
  }
}

export function* watchLapsSaga() {
  yield takeLatest(fetchLapRequest.type, fetchLapSaga);
  yield takeLatest(fetchLapsRequest.type, fetchLapsSaga);
  yield takeLatest(createLapRequest.type, createLapSaga);
  yield takeLatest(updateLapRequest.type, updateLapSaga);
  yield takeLatest(deleteLapRequest.type, deleteLapSaga);
  yield takeLatest(publishLapRequest.type, publishLapSaga);
  
}